import React, { useState, useEffect } from 'react';
import { useAgentContext } from '../contexts/AgentContext';
import { UserSettings } from '../types/index';
import { getBackendUrl } from '../lib/backend';
import { parseApiError } from '../lib/api';

const DEFAULT_PREFERENCES: UserSettings = {
  workingHours: {
    start: '09:00',
    end: '17:00',
  },
  defaultMeetingDuration: 30,
  timezone: 'America/Mexico_City',
  availableDays: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  },
  assistantEnabled: true,
  appointmentsEnabled: true,
  humanTransferEnabled: true,
  googleCalendarId: '',
};

export const usePreferencesState = () => {
  const [preferences, setPreferences] = useState<UserSettings>(DEFAULT_PREFERENCES);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [waTokenSaveNonce, setWaTokenSaveNonce] = useState(0);
  const {
    preferences: contextPreferences,
    preferencesLoading,
    setPreferences: setContextPreferences,
    refreshUserData,
    refreshPreferences,
  } = useAgentContext();

  useEffect(() => {
    if (contextPreferences) {
      setPreferences({
        ...DEFAULT_PREFERENCES,
        ...contextPreferences,
      });
    }
  }, [contextPreferences]);

  const loading = preferencesLoading && !contextPreferences;

  const handleSubmit = async (e: React.FormEvent, prefsOverride?: UserSettings) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const prefsToSave = prefsOverride ?? preferences;

    try {
      const { waAccessToken, ...rest } = prefsToSave;
      const payload: UserSettings & { waAccessToken?: string } = { ...rest };
      if (waAccessToken?.trim()) {
        payload.waAccessToken = waAccessToken.trim();
      }

      const response = await fetch(`${getBackendUrl()}/api/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await parseApiError(response, 'Error al guardar preferencias');
      }

      setSuccessMessage('¡Preferencias guardadas exitosamente! ✨');
      const saved = await response.json();
      const { waAccessToken: _removed, ...safePreferences } = saved;
      setPreferences((prev) => ({ ...prev, ...safePreferences, waAccessToken: undefined }));
      setContextPreferences({ ...safePreferences, waAccessToken: undefined });
      if (waAccessToken?.trim()) {
        setWaTokenSaveNonce((nonce) => nonce + 1);
      }

      await refreshUserData();
      await refreshPreferences();
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'Error al guardar preferencias');
    }
  };

  return {
    preferences,
    setPreferences,
    loading,
    error,
    successMessage,
    handleSubmit,
    setError,
    setSuccessMessage,
    waTokenSaveNonce,
  };
};
